// «Разработка» (PLAN §2 «Режим разработчика»): правки платформы агентом. Порядок подачи как везде:
// что происходит → заявки → релизы. Каждая правка: независимая проверка, снимки «до / после», решение владельца,
// выкатка с 10 минутами наблюдения и автооткатом; откат — в одну кнопку. Выкатка ждёт тихого окна — не обрывает
// работу агента (deploy.state.waitingFor — чего ждёт); не дождалась — релиз «отложен» с причиной (note).
import { Button, Card, Chip, dateShort, EmptyState, Skeleton, time, toast, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Code2, RotateCcw, Wrench } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { api } from '../api';
import { PageHead } from './common';

export interface CrRow {
  id: number;
  title: string;
  task: string;
  status: string;
  statusRu: string;
  summary: string | null;
  error: string | null;
  requestedBy: string;
  protection: { verdict: 'ok' | 'confirm' | 'blocked'; reason: string | null } | null;
  releaseId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Release {
  id: string;
  sha: string;
  source: string;
  crId: number | null;
  status: string;
  previousId: string | null;
  switchMs: number | null;
  watchUntil: string | null;
  note: string | null;
  createdAt: string;
  deployedAt: string | null;
}

/** Что держит переключение на новую версию: работа агента и с какого времени. */
interface Busy {
  what: string;
  since: string | null;
}

interface DevView {
  deployer: { alive: boolean; at: string | null };
  state: { phase?: string; release?: string; until?: string; since?: string; waitingFor?: Busy[] };
  crs: CrRow[];
  releases: Release[];
}

type Tone = 'default' | 'accent' | 'success' | 'warning' | 'danger' | 'info';
export const CR_TONE: Record<string, Tone> = {
  queued: 'default',
  preparing: 'info',
  working: 'info',
  submitted: 'info',
  checking: 'info',
  ready: 'accent',
  failed: 'danger',
  approved: 'info',
  deploying: 'info',
  watching: 'warning',
  deployed: 'success',
  rolled_back: 'danger',
  rejected: 'default',
  cancelled: 'default',
};

const REL_STATUS: Record<string, { label: string; tone: Tone }> = {
  building: { label: 'собирается', tone: 'info' },
  active: { label: 'работает', tone: 'success' },
  previous: { label: 'прошлый', tone: 'default' },
  rolled_back: { label: 'откачен', tone: 'danger' },
  postponed: { label: 'отложен', tone: 'warning' },
  failed: { label: 'не поднялся', tone: 'danger' },
  pruned: { label: 'удалён', tone: 'default' },
};

const SOURCE: Record<string, string> = { cr: 'правка агента', owner: 'ваше обновление', rollback: 'откат' };

const seconds = (ms: number | null) => (ms === null ? '—' : `${Math.max(1, Math.round(ms / 1000))} с`);

/** «батч креативов с 18:40» — сегодня только время, раньше — дата и время. */
const busyText = (b: Busy) => {
  if (!b.since) return b.what;
  return `${b.what} с ${dateShort(b.since) === 'сегодня' ? time(b.since) : when(b.since)}`;
};

/** Причина, по которой релиз отложен, — без повтора «Выкатка отложена:». */
const postponedWhy = (note: string | null) => note?.replace(/^Выкатка отложена:\s*/i, '').trim() || null;

function Ask() {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [task, setTask] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api<{ id: number }>('/api/dev/cr', { title, task });
      toast(`Заявка #${r.id}: готовлю копию кода, агент возьмётся за правку`);
      setTitle('');
      setTask('');
      await qc.invalidateQueries({ queryKey: ['dev'] });
    } catch (err) {
      toast((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Попросить правку">
      <form className="dev-ask" onSubmit={submit}>
        <input
          className="input"
          placeholder="Коротко: «Кнопка «Одобрить» крупнее на телефоне»"
          aria-label="Название правки"
          maxLength={120}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <textarea
          className="input"
          rows={3}
          placeholder="Что поменять и как понять, что готово"
          aria-label="Задание"
          maxLength={4000}
          value={task}
          onChange={(e) => setTask(e.target.value)}
        />
        <div className="dev-ask-row">
          <span className="muted">
            Агент делает правку в отдельной копии кода, платформа проверяет её сама. Ставите её на платформу
            только вы.
          </span>
          <Button
            type="submit"
            variant="primary"
            disabled={busy || title.trim().length < 3 || task.trim().length < 10}
          >
            <Wrench />
            Попросить
          </Button>
        </div>
      </form>
    </Card>
  );
}

export function DevPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['dev'], queryFn: () => api<DevView>('/api/dev'), refetchInterval: 10_000 });
  const d = q.data;
  const active = d?.releases.find((r) => r.status === 'active');
  const [busy, setBusy] = useState(false);
  const rollback = async (releaseId?: string) => {
    setBusy(true);
    try {
      await api('/api/dev/rollback', releaseId ? { releaseId } : {});
      toast('Откат поставлен — обычно это меньше минуты');
      await qc.invalidateQueries({ queryKey: ['dev'] });
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const phase = d?.state.phase ?? 'idle';
  const waiting = phase === 'draining' ? (d?.state.waitingFor ?? []) : [];
  return (
    <>
      <PageHead
        title="Разработка"
        sub="Правки платформы агентом: независимая проверка, ваше решение, установка с наблюдением и откатом"
      />
      <div className="pc pc-read">
        {q.isPending && <Skeleton h={120} />}
        {d && (
          <>
            <Card title="Сейчас">
              <div className="dev-now">
                <div>
                  <span>Установка правок</span>
                  <Chip tone={d.deployer.alive ? 'success' : 'danger'} dot>
                    {d.deployer.alive ? 'работает' : 'не отвечает'}
                  </Chip>
                </div>
                <div>
                  <span>Работает версия</span>
                  <b className="mono">{active?.id ?? '—'}</b>
                  {active && <small className="muted">{SOURCE[active.source] ?? active.source}</small>}
                </div>
                <div>
                  <span>Состояние</span>
                  <b>
                    {phase === 'draining'
                      ? 'новая версия ждёт, пока агент доделает работу'
                      : phase === 'watching' && d.state.until
                        ? `наблюдение до ${time(d.state.until)}`
                        : 'спокойно'}
                  </b>
                </div>
                {active?.previousId && (
                  <Button disabled={busy} onClick={() => rollback()}>
                    <RotateCcw />
                    Откатить на прошлую
                  </Button>
                )}
              </div>
              {phase === 'draining' && (
                <div className="dev-wait">
                  <p>
                    Переключение не обрывает работу агента: новые фоновые работы подождут, текущие
                    доделываются — до 4 часов. Не дождётся — установка будет отложена, работающая версия
                    останется.
                  </p>
                  {waiting.length > 0 ? (
                    <ul>
                      {waiting.map((b) => (
                        <li key={`${b.what}:${b.since}`}>Идёт {busyText(b)}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="muted">Агент свободен — переключение вот-вот начнётся.</p>
                  )}
                </div>
              )}
              {!d.deployer.alive && (
                <p className="dev-warn">
                  Установка правок не отвечает: правки не проверяются и не ставятся. Она запускается вместе с
                  обновлением платформы на сервере.
                </p>
              )}
            </Card>

            <Ask />

            <Card title="Заявки" count={d.crs.length}>
              {d.crs.length ? (
                <ul className="dev-crs">
                  {d.crs.map((c) => (
                    <li key={c.id}>
                      <Link to="/dev/$id" params={{ id: String(c.id) }} className="dev-cr">
                        <span className="mono muted">#{c.id}</span>
                        <b>{c.title}</b>
                        <Chip tone={CR_TONE[c.status] ?? 'default'} dot={c.status === 'ready'}>
                          {c.statusRu}
                        </Chip>
                        {c.protection?.verdict === 'confirm' && <Chip tone="warning">защищённая зона</Chip>}
                        <span className="muted dev-when">{when(c.updatedAt)}</span>
                      </Link>
                      {c.error && c.status === 'failed' && <p className="dev-err">{c.error}</p>}
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState icon={<Code2 />} title="Правок ещё не было">
                  Попросите правку выше или в чате с агентом: «сделай …». Агент сделает её в отдельной копии
                  кода, а вы решите, ставить ли её.
                </EmptyState>
              )}
            </Card>

            {d.releases.length > 0 && (
              <Card title="Релизы">
                <div className="fin-tbl-wrap">
                  <table className="fin-tbl dev-rel">
                    <thead>
                      <tr>
                        <th>Версия</th>
                        <th>Откуда</th>
                        <th>Состояние</th>
                        <th className="num hide-m">Переключение</th>
                        <th className="hide-m">Когда</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {d.releases.map((r) => {
                        const st = REL_STATUS[r.status] ?? { label: r.status, tone: 'default' as Tone };
                        return (
                          <tr key={r.id}>
                            <td className="mono">{r.id}</td>
                            <td>
                              {r.crId ? (
                                <Link to="/dev/$id" params={{ id: String(r.crId) }}>
                                  правка #{r.crId}
                                </Link>
                              ) : (
                                (SOURCE[r.source] ?? r.source)
                              )}
                            </td>
                            <td>
                              <Chip tone={st.tone}>{st.label}</Chip>
                              {r.status === 'postponed' && postponedWhy(r.note) && (
                                <small className="dev-why">{postponedWhy(r.note)}</small>
                              )}
                            </td>
                            <td className="num hide-m">{seconds(r.switchMs)}</td>
                            <td className="hide-m muted">{r.deployedAt ? when(r.deployedAt) : '—'}</td>
                            <td className="num">
                              {r.status === 'previous' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={busy}
                                  onClick={() => rollback(r.id)}
                                >
                                  Вернуть
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </>
  );
}
